import React, { useState, useEffect } from 'react';
import { useButtonLinks } from '../hooks/useButtonLinks';
import { usePDFDocuments } from '../hooks/usePDFDocuments';
import { useTranslation } from '../hooks/useTranslation';
import PDFModal from '../components/PDFModal';

const Regulations = () => {
  const { handleButtonClick } = useButtonLinks();
  const { getDocumentsByCategory, getDocumentUrl } = usePDFDocuments();
  const { t } = useTranslation();
  const [modalState, setModalState] = useState({
    isOpen: false,
    pdfUrl: '',
    title: ''
  });

  const openPDFModal = (document) => {
    setModalState({
      isOpen: true,
      pdfUrl: getDocumentUrl(document),
      title: document.name
    });
  };

  const closePDFModal = () => {
    setModalState({
      isOpen: false,
      pdfUrl: '',
      title: ''
    });
  };

  return (
    <div className="regulations-page">
      {/* Hero Section */}
      <section className="regulations-hero">
        <div className="container">
          <div className="regulations-hero-content">
            <h1 className="regulations-title">{t('regulations.hero.title')}</h1>
            <p className="regulations-subtitle">
              {t('regulations.hero.subtitle')}
            </p>
            <div className="hero-question">
              <h2>{t('regulations.hero.ready')}</h2>
            </div>
            <div className="regulations-features">
              <div className="feature-highlight">
                <i className="fas fa-users"></i>
                <span>{t('regulations.hero.features.clients')}</span>
              </div>
              <div className="feature-highlight">
                <i className="fas fa-chart-line"></i>
                <span>{t('regulations.hero.features.assets')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Regulations Documents Section */}
      <section className="regulations-documents">
        <div className="container">
          <h2 className="section-title">{t('regulations.documents.title')}</h2>
          
          <div className="regulations-grid">
            {/* Regulatory Documents */}
            {getDocumentsByCategory('other').length > 0 && (
              <div className="regulation-category">
                <h3 className="category-title">{t('regulations.documents.categories.other')}</h3>
                <div className="documents-list">
                  {getDocumentsByCategory('other').map(document => (
                    <div key={document.id} className="document-item">
                      <i className={document.icon}></i>
                      <span>{document.name}</span>
                      <button 
                        className="btn btn-sm btn-primary" 
                        onClick={() => openPDFModal(document)}
                      >
                        {t('regulations.documents.preview')}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="regulations-cta">
            <h3>{t('regulations.documents.cta.title')}</h3>
            <button 
              className="btn btn-primary btn-large"
              onClick={(e) => handleButtonClick('getStarted', e)}
            >
              {t('regulations.documents.cta.button')}
            </button>
          </div>
        </div>
      </section>

      {/* Negative Balance Protection Section */}
      <section className="negative-balance-protection">
        <div className="container">
          <h2 className="section-title">{t('regulations.negativeBalance.title')}</h2>
          <p className="section-subtitle">{t('regulations.negativeBalance.subtitle')}</p>
          
          <div className="protection-content">
            <div className="protection-explanation">
              <p>
                {t('regulations.negativeBalance.explanation')}
              </p>
            </div>
            
            <div className="protection-feature">
              <div className="protection-icon">
                <i className="fas fa-shield-alt"></i>
              </div>
              <h3>{t('regulations.negativeBalance.feature')}</h3>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Help Section */}
      <section className="quick-help">
        <div className="container">
          <h2 className="section-title">{t('regulations.quickHelp.title')}</h2>
          
          <div className="help-sections">
            {/* Experienced Trader Section */}
            <div className="help-section">
              <h3 className="help-title">{t('regulations.quickHelp.experienced.title')}</h3>
              <div className="help-cards">
                <div className="help-card">
                  <div className="help-icon">
                    <i className="fas fa-handshake"></i>
                  </div>
                  <h4>{t('regulations.quickHelp.experienced.partner.title')}</h4>
                  <p>{t('regulations.quickHelp.experienced.partner.description')}</p>
                </div>
                <div className="help-card">
                  <div className="help-icon">
                    <i className="fas fa-globe-americas"></i>
                  </div>
                  <h4>{t('regulations.quickHelp.experienced.markets.title')}</h4>
                  <p>{t('regulations.quickHelp.experienced.markets.description')}</p>
                </div>
                <div className="help-card">
                  <div className="help-icon">
                    <i className="fas fa-calendar-alt"></i>
                  </div>
                  <h4>{t('regulations.quickHelp.experienced.events.title')}</h4>
                  <p>{t('regulations.quickHelp.experienced.events.description')}</p>
                </div>
              </div>
            </div>

            {/* New Trader Section */}
            <div className="help-section">
              <h3 className="help-title">{t('regulations.quickHelp.newTrader.title')}</h3>
              <div className="help-cards">
                <div className="help-card">
                  <div className="help-icon">
                    <i className="fas fa-mouse-pointer"></i>
                  </div>
                  <h4>{t('regulations.quickHelp.newTrader.platform.title')}</h4>
                  <p>{t('regulations.quickHelp.newTrader.platform.description')}</p>
                </div>
                <div className="help-card">
                  <div className="help-icon">
                    <i className="fas fa-chart-bar"></i>
                  </div>
                  <h4>{t('regulations.quickHelp.newTrader.charts.title')}</h4>
                  <p>{t('regulations.quickHelp.newTrader.charts.description')}</p>
                </div>
                <div className="help-card">
                  <div className="help-icon">
                    <i className="fas fa-play-circle"></i>
                  </div>
                  <h4>{t('regulations.quickHelp.newTrader.demo.title')}</h4>
                  <p>{t('regulations.quickHelp.newTrader.demo.description')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="regulations-final-cta">
        <div className="container">
          <div className="cta-content">
            <h2>{t('regulations.finalCta.title')}</h2>
            <p>{t('regulations.finalCta.description')}</p>
            <div className="cta-buttons">
              <button 
                className="btn btn-primary btn-large"
                onClick={(e) => handleButtonClick('openAccount', e)}
              >
                {t('regulations.finalCta.openButton')}
              </button>
              <button className="btn btn-secondary btn-large">{t('regulations.finalCta.downloadButton')}</button>
            </div>
          </div>
        </div>
      </section>
      
      {/* PDF Modal */}
      <PDFModal 
        isOpen={modalState.isOpen}
        onClose={closePDFModal}
        pdfUrl={modalState.pdfUrl}
        title={modalState.title}
      />
    </div>
  );
};

export default Regulations;